/* 3D jerseys on hangers — hero slide. Markup: snippets/jersey-hanger-hero.liquid
   Each jersey is its outline "inflated" into a thin soft volume (front + back photo)
   on a turned-wood hanger, all hanging from one chrome rail. Dragging a jersey turns
   its hanger; simple spring physics make the cloth lag, sway, flare and ripple.
   Three.js loads only when the slide is first shown, and the scene renders only
   while it is on screen. */
const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.js';

// Phones get the lite build: 512px textures, only the zoomed jersey at full size (the full build crashed iOS Safari)
const PHONE = matchMedia('(hover: none), (pointer: coarse), (max-width: 749px)').matches;
function init(root) {
  if (root.dataset.hangerReady) return;
  root.dataset.hangerReady = 'true';
  const slide = root.closest('.kave-hero__slide');
  let started = false;
  let active = false;
  function sync() {
    active = !slide || slide.classList.contains('is-active');
    // light slide: darken the transparent header + slider controls while it shows
    if (slide) document.documentElement.classList.toggle('jersey-hanger-light', active);
    if (active && root.jerseyHangerWake) root.jerseyHangerWake();
    if (active && !started) { started = true; start(root, () => active).catch(err => console.warn('[jersey-hanger]', err)); }
  }
  if (slide) new MutationObserver(sync).observe(slide, { attributes: true, attributeFilter: ['class'] });
  sync();
}

async function start(root, isActive) {
  const THREE = await import(THREE_URL);
  // product-page dialog (snippets/garment-3d.liquid): `single` = one garment, always up close; `family` = the
  // product's colourways on a small rail, opened up close on the current one (data-start)
  const single = 'single' in root.dataset, family = 'family' in root.dataset;
  const jerseys = JSON.parse(root.querySelector('[data-jersey-hanger-config]').textContent)
    .map(j => ({ brand: '', name: '', colour: '', price: '', url: '', shop: '', ...(j.h3d || {}), ...j }));
  const stage = root.querySelector('[data-jersey-hanger-stage]');
  const angleEl = root.querySelector('[data-jersey-hanger-angle]');
  const small = matchMedia('(max-width: 749px)').matches;
  const lite = PHONE;                                     // phone GPU memory: ~45 MB of textures instead of ~170 MB
  const BG = '#e7e5e0';
  const SPACING = (+root.dataset.spacing || 1) * (small ? 0.16 : 0.23);   // side-on hangers sit tight along the rail
  // draw only while something moves (see wake / tick at the end)
  let running = false, calm = 0, last = 0, lost = false;
  const PUSH = 0.3;                                       // neighbours slide this far aside for a front-facing jersey

  // ── Renderer / scene ───────────────────────────────────────────
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = lite ? THREE.PCFShadowMap : THREE.PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = !lite;                  // lite: redraw the wall shadow every other frame (see tick)
  let shadowFrame = 0;
  renderer.domElement.className = 'jersey-hanger__canvas';
  stage.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(BG);
  // small studio environment for the chrome and the wood's sheen
  const envScene = new THREE.Scene();
  envScene.add(new THREE.Mesh(new THREE.BoxGeometry(10, 10, 10), new THREE.MeshBasicMaterial({ color: '#8d8a85', side: THREE.BackSide })));
  for (const [x, y, z, w, h, c] of [[0, 4.9, 0, 6, 6, '#ffffff'], [-4.9, 1, 1, 3, 5, '#fff3e6'], [4.9, 0, -1, 3, 4, '#e8eeff'], [0, -1, 4.9, 8, 2, '#d9d6d0']]) {
    const p = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: c, side: THREE.DoubleSide }));
    p.position.set(x, y, z); p.lookAt(0, 0, 0); envScene.add(p);
  }
  scene.environment = new THREE.PMREMGenerator(renderer).fromScene(envScene, 0.04).texture;
  scene.environmentIntensity = 0.55;

  const camera = new THREE.PerspectiveCamera(28, 1, 0.05, 40);
  scene.add(new THREE.HemisphereLight('#ffffff', '#cfc8bd', 0.9));
  const key = new THREE.DirectionalLight('#fff6ec', 2.1);
  key.position.set(-1.1, 1.6, 2.2);
  key.castShadow = true;
  key.shadow.mapSize.set(small ? 512 : 1024, small ? 512 : 1024);
  Object.assign(key.shadow.camera, { left: -2, right: 2, top: 0.6, bottom: -1.6, near: 0.5, far: 6 });
  key.shadow.radius = 8;
  key.shadow.bias = -0.0004;
  scene.add(key);
  const rim = new THREE.DirectionalLight('#dfe8ff', 0.8);
  rim.position.set(1.4, 0.6, -1.5);
  scene.add(rim);

  // pegboard wall
  const peg = document.createElement('canvas'); peg.width = peg.height = 256;
  const pg = peg.getContext('2d');
  pg.fillStyle = BG; pg.fillRect(0, 0, 256, 256);
  pg.fillStyle = 'rgba(27,27,28,.10)';
  for (let y = 16; y < 256; y += 32) for (let x = 16; x < 256; x += 32) { pg.beginPath(); pg.arc(x, y, 3.2, 0, Math.PI * 2); pg.fill(); }
  const pegTex = new THREE.CanvasTexture(peg);
  pegTex.wrapS = pegTex.wrapT = THREE.RepeatWrapping; pegTex.repeat.set(30, 18); pegTex.colorSpace = THREE.SRGBColorSpace;
  const wall = new THREE.Mesh(new THREE.PlaneGeometry(20, 12), new THREE.MeshStandardMaterial({ map: pegTex, roughness: 0.95 }));
  wall.position.set(0, -0.4, -0.55);
  wall.receiveShadow = true;
  scene.add(wall);

  // shared materials
  const chrome = new THREE.MeshStandardMaterial({ color: '#d9d9d9', metalness: 1, roughness: 0.18 });
  const wc = document.createElement('canvas'); wc.width = 1024; wc.height = 64;
  const wg = wc.getContext('2d');
  const grad = wg.createLinearGradient(0, 0, 0, 64);
  grad.addColorStop(0, '#b27a4b'); grad.addColorStop(0.5, '#9b6340'); grad.addColorStop(1, '#7d4c2d');
  wg.fillStyle = grad; wg.fillRect(0, 0, 1024, 64);
  for (let i = 0; i < 70; i++) {
    const y = Math.random() * 64;
    wg.strokeStyle = `rgba(${Math.random() < 0.5 ? '70,38,20' : '215,160,110'},${0.08 + Math.random() * 0.14})`;
    wg.lineWidth = 0.6 + Math.random() * 1.8; wg.beginPath(); wg.moveTo(0, y);
    for (let x = 0; x <= 1024; x += 32) wg.lineTo(x, y + Math.sin(x * 0.01 + i) * 2.5);
    wg.stroke();
  }
  const woodTex = new THREE.CanvasTexture(wc); woodTex.colorSpace = THREE.SRGBColorSpace; woodTex.anisotropy = 8;
  const wood = new THREE.MeshStandardMaterial({ map: woodTex, roughness: 0.42, envMapIntensity: 0.7 });

  const RAIL_Y = 0.2;
  const rail = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, 14, 32), chrome);
  rail.rotation.z = Math.PI / 2; rail.position.set(0, RAIL_Y, 0); rail.castShadow = true;
  scene.add(rail);

  const texLoader = new THREE.TextureLoader();
  texLoader.setCrossOrigin('anonymous');
  const loadTex = src => new Promise((res, rej) => texLoader.load(src, t => { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = lite ? 4 : 8; res(t); }, undefined, rej));
  const models = new Map();
  const loadModel = url => { if (!models.has(url)) models.set(url, fetch(url).then(r => r.json())); return models.get(url); };
  const loadImg = src => new Promise((res, rej) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => res(i); i.onerror = rej; i.src = src; });

  const DEFORM = `
    float hang = clamp(-position.y / uLen, 0.0, 1.0);
    float lag = pow(hang, 1.35);
    float tw = uTwist * lag;
    float ct = cos(tw), st = sin(tw);
    float sleeve = smoothstep(0.23, 0.33, abs(position.x));
    float ph = hang * 9.0 - uTime * 7.5 + position.x * 5.0;
  `;
  function patch(shader, cloth, withNormals) {
    Object.assign(shader.uniforms, cloth);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>
        uniform float uTime, uLen, uTwist, uFlare, uBend, uRipple, uSleeve;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        ${withNormals ? '' : DEFORM}
        transformed.xz = mat2(ct, st, -st, ct) * transformed.xz;
        transformed.xz *= 1.0 + uFlare * hang * hang;
        transformed.y += uFlare * 0.06 * hang * hang;
        transformed.z += uBend * hang * hang;
        transformed.z += uRipple * sin(ph) * hang + uRipple * 0.5 * sin(ph * 1.7 + 1.3) * hang;
        transformed.x += uRipple * 0.35 * cos(ph * 0.8) * hang;
        transformed.z += uSleeve * sleeve * (0.4 + hang);
      `);
    if (withNormals) {
      shader.vertexShader = shader.vertexShader.replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>
        ${DEFORM}
        objectNormal.xz = mat2(ct, st, -st, ct) * objectNormal.xz;
        objectNormal.z += -uRipple * 5.4 * cos(ph) * hang;
        objectNormal = normalize(objectNormal);
      `);
    }
  }

  // ── One jersey on its hanger ────────────────────────────────────
  const REST = -1.4;                                      // ~80°: side-on, like a real rail
  // side-on as seen from the camera (not from straight ahead), plus a touch of variety
  let camDist = 4;
  const facing = index => -Math.atan2((index - (jerseys.length - 1) / 2) * SPACING, camDist);
  const restFor = index => REST + facing(index) + ((index % 3) - 1) * 0.04;
  async function buildJersey(cfg, index) {
    const meta = cfg.meta;
    const S = 1;
    const [, by0, , by1] = meta.bbox;
    const topV = by0, LEN = (by1 - by0) * S;
    // volume: casual cotton (tees, sweatshirts) hangs fuller than a race jersey; 1 = jersey
    const VOL = cfg.volume || 1;
    const T_TOP = 0.03 * VOL, T_HEM = 0.011 * VOL, EDGE = 0.04 * (1 + 0.6 * (VOL - 1));
    const R = lite ? 80 : small ? 90 : 130;              // mesh resolution: smooth enough, ~⅓ of the old triangle count
    // below the shoulders thickness stays 0 for the outer ~1.6 mesh cells: the cells the outline cuts through
    // are flat, so front and back meet in one clean line seen side-on (otherwise the grid shows as teeth).
    // Across the shoulders the band stays thin, or the hanger's wood would poke through the seam.
    const SEAM_TOP = (VOL > 1 ? Math.max(6, 1.6 * meta.N / R) : 6) / meta.N * S, SEAM_SIDE = Math.max(6, 1.6 * meta.N / R) / meta.N * S;
    const seamAt = v => THREE.MathUtils.lerp(SEAM_TOP, SEAM_SIDE, THREE.MathUtils.smoothstep(v, by0 + 0.1, by0 + 0.2));

    const src = lite ? cfg.lite : cfg;
    const distImg = await loadImg(src.dist);
    const DN = distImg.naturalWidth || meta.N;           // the lite distance field is 256px; values stay in meta.N units
    const dc = document.createElement('canvas'); dc.width = dc.height = DN;
    dc.getContext('2d').drawImage(distImg, 0, 0, DN, DN);
    const dpx = dc.getContext('2d').getImageData(0, 0, DN, DN).data;
    dc.width = dc.height = 0;
    const distAt = (u, v) => {
      const x = Math.min(DN - 1, Math.max(0, Math.round(u * (DN - 1))));
      const y = Math.min(DN - 1, Math.max(0, Math.round(v * (DN - 1))));
      return dpx[(y * DN + x) * 4] / 255 * meta.distMaxPx / meta.N * S;
    };
    const thickness = (d, v) => {
      d -= seamAt(v);
      if (d <= 0) return 0;
      if (VOL > 1) {
        // casual cotton: two flat layers with a gap, rounded only at the seams (a dome reads as a stuffed pillow)
        const hang = THREE.MathUtils.clamp((v - topV) / (by1 - by0), 0, 1);
        const k = THREE.MathUtils.smoothstep(d, 0, 0.022);
        return THREE.MathUtils.lerp(0.022, 0.016, hang) * VOL * 0.5 * k;
      }
      const k = Math.min(d / EDGE, 1);
      const round = Math.sqrt(1 - (1 - k) * (1 - k));
      const hang = THREE.MathUtils.clamp((v - topV) / (by1 - by0), 0, 1);
      return THREE.MathUtils.lerp(T_TOP, T_HEM, Math.pow(hang, 0.7)) * round + 0.004 * (1 - Math.exp(-Math.max(0, d - EDGE) / 0.08));
    };
    const zs = new Float32Array((R + 1) * (R + 1)), inside = new Uint8Array((R + 1) * (R + 1)), ds = new Float32Array((R + 1) * (R + 1));
    for (let j = 0; j <= R; j++) for (let i = 0; i <= R; i++) {
      const d = distAt(i / R, j / R);
      inside[j * (R + 1) + i] = d > 0 ? 1 : 0; ds[j * (R + 1) + i] = d;
    }
    if (VOL > 1) {
      // casual garments: the hem is an opening, not a seam. Near the bottom, thickness follows the distance to
      // the side seams only (along the row), so front and back stay apart right down to the hem edge.
      // Sleeves hang flat (fabric, not an inflated tube) and keep less than a jersey's thickness.
      const hemRow = Math.min(R, Math.round((by1 - 0.03) * R));
      let hl = R, hr = 0;
      for (let i = 0; i <= R; i++) if (inside[hemRow * (R + 1) + i]) { hl = Math.min(hl, i); hr = Math.max(hr, i); }
      const torsoHalf = Math.max(0.15, (hr - hl) / R / 2), cx = (hl + hr) / R / 2;
      for (let j = 0; j <= R; j++) {
        const v = j / R, open = THREE.MathUtils.smoothstep(v, by1 - 0.14, by1 - 0.05);
        let start = -1;
        for (let i = 0; i <= R + 1; i++) {
          const inn = i <= R && inside[j * (R + 1) + i];
          if (inn && start < 0) start = i;
          if (!inn && start >= 0) {
            for (let k = start; k < i; k++) {
              const n = j * (R + 1) + k;
              const side = Math.min(k - start + 1, i - k) / R * S;            // distance to the run's ends along the row
              const d = THREE.MathUtils.lerp(ds[n], Math.max(ds[n], side), open);
              const sleeve = THREE.MathUtils.smoothstep(Math.abs(k / R - cx), torsoHalf, torsoHalf + 0.05) * (1 - open);
              zs[n] = thickness(d, v) * THREE.MathUtils.lerp(1, 0.32 / VOL, sleeve);
            }
            start = -1;
          }
        }
      }
      // the grid row just past the hem edge keeps the hem's thickness, so the last cells don't pinch it shut
      for (let j = 1; j <= R; j++) {
        if (THREE.MathUtils.smoothstep(j / R, by1 - 0.14, by1 - 0.05) < 0.5) continue;
        for (let i = 0; i <= R; i++) {
          const n = j * (R + 1) + i, up = n - (R + 1);
          if (!inside[n] && inside[up]) zs[n] = zs[up];
        }
      }
    } else {
      for (let n = 0; n < zs.length; n++) zs[n] = thickness(ds[n], Math.floor(n / (R + 1)) / R);
    }
    function surface(sign, depthScale, rows = null) {
      const pos = new Float32Array((R + 1) * (R + 1) * 3), uv = new Float32Array((R + 1) * (R + 1) * 2), idx = [];
      for (let j = 0, n = 0; j <= R; j++) for (let i = 0; i <= R; i++, n++) {
        pos[n * 3] = (i / R - 0.5) * S; pos[n * 3 + 1] = -(j / R - topV) * S; pos[n * 3 + 2] = sign * (zs[n] * depthScale + (depthScale > 0 ? 0.0012 : 0));   // a hair apart: never z-fight
        uv[n * 2] = i / R; uv[n * 2 + 1] = 1 - j / R;
      }
      const at = (i, j) => j * (R + 1) + i;
      for (let j = 0; j < R; j++) for (let i = 0; i < R; i++) {
        const a = at(i, j), b = at(i + 1, j), c = at(i, j + 1), d = at(i + 1, j + 1);
        if (!(inside[a] || inside[b] || inside[c] || inside[d])) continue;
        if (rows && !rows(j / R)) continue;
        if (sign > 0) idx.push(a, c, b, b, c, d); else idx.push(a, b, c, b, d, c);
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
      g.setIndex(idx);
      g.computeVertexNormals();
      return g;
    }

    const cloth = { uTime: { value: 0 }, uLen: { value: LEN }, uTwist: { value: 0 }, uFlare: { value: 0 }, uBend: { value: 0 }, uRipple: { value: 0 }, uSleeve: { value: 0 } };
    const texSrcs = [src.front, src.back].concat(cfg.lining ? [src.lining] : []);
    const [frontTex, backTex, liningTex] = await Promise.all(texSrcs.map(loadTex));

    const baseX = (index - (jerseys.length - 1) / 2) * SPACING;
    const pivot = new THREE.Group(); pivot.position.set(baseX, RAIL_Y, 0);
    const swing = new THREE.Group(); pivot.add(swing);
    const spin = new THREE.Group(); swing.add(spin);
    const body = new THREE.Group(); body.position.y = -RAIL_Y; spin.add(body);

    const hitMeshes = [];
    // the inside of the jersey: one flat colour, the same as its collar lining
    const insideColor = new THREE.Color(cfg.inside || "#8a8a8a");
    function jerseyMesh(tex, sign, depthScale, twoSided = true, rows = null) {
      const mat = new THREE.MeshStandardMaterial({ map: tex, alphaTest: 0.5, roughness: 0.82, envMapIntensity: 0.6, side: twoSided ? THREE.DoubleSide : THREE.FrontSide });
      mat.onBeforeCompile = s => {
        patch(s, cloth, true);
        s.uniforms.uInside = { value: insideColor };
        // inside colour only where the jersey has body; along the flat seam band the back faces keep the
        // fabric colour, so nothing serrated or see-through shows at the edge
        s.vertexShader = s.vertexShader
          .replace('#include <common>', '#include <common>\nvarying float vThick;')
          .replace('#include <begin_vertex>', '#include <begin_vertex>\nvThick = abs(position.z);');
        s.fragmentShader = s.fragmentShader
          .replace('#include <common>', '#include <common>\nuniform vec3 uInside;\nvarying float vThick;')
          .replace('#include <map_fragment>', '#include <map_fragment>\nif (!gl_FrontFacing) diffuseColor.rgb = mix(diffuseColor.rgb, uInside, smoothstep(0.002, 0.008, vThick));');
      };
      const depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, map: tex, alphaTest: 0.5 });
      depth.onBeforeCompile = s => patch(s, cloth, false);
      const m = new THREE.Mesh(surface(sign, depthScale, rows), mat);
      m.customDepthMaterial = depth;
      m.castShadow = true; m.receiveShadow = true;
      body.add(m); hitMeshes.push(m);
      return m;
    }
    // a real garment mesh (cfg.model: JSON with the shirt's position/normal/uv/index): our front and back
    // photos are projected onto it, fitted to the photo's outline box, so the print stays the real one while
    // the shape (tube sleeves, open hem, folds) comes from the model
    async function modelMesh() {
      const sh = (await loadModel(cfg.model)).Shirt, P = sh.position, Nn = sh.normal;
      let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity, z0 = Infinity, z1 = -Infinity;
      for (let i = 0; i < P.length; i += 3) {
        x0 = Math.min(x0, P[i]); x1 = Math.max(x1, P[i]); y0 = Math.min(y0, P[i + 1]); y1 = Math.max(y1, P[i + 1]);
        z0 = Math.min(z0, P[i + 2]); z1 = Math.max(z1, P[i + 2]);
      }
      const [bx0, , bx1] = meta.bbox, flip = cfg.modelFlip ? -1 : 1;
      const sx = (bx1 - bx0) * S / (x1 - x0), sy = LEN / (y1 - y0), sz = sx * (cfg.modelDepth || 1);
      const cxP = ((bx0 + bx1) / 2 - 0.5) * S, cxm = (x0 + x1) / 2, czm = (z0 + z1) / 2;
      const n = P.length / 3, pos = new Float32Array(n * 3), nrm = new Float32Array(n * 3), puv = new Float32Array(n * 2), side = new Float32Array(n), edge = new Float32Array(n);
      for (let k = 0; k < n; k++) {
        const X = (P[3 * k] - cxm) * flip, Y = P[3 * k + 1], Z = (P[3 * k + 2] - czm) * flip;
        // less boxy: the body narrows from the armpits to the hem (sleeves above stay as modelled)
        const taper = 1 - (1 - (cfg.modelTaper || 0.87)) * THREE.MathUtils.smoothstep((y1 - Y) / (y1 - y0), 0.32, 1);
        pos[3 * k] = X * sx * taper + cxP; pos[3 * k + 1] = (Y - y1) * sy; pos[3 * k + 2] = Z * sz;
        let nx = Nn[3 * k] * flip / sx, ny = Nn[3 * k + 1] / sy, nz = Nn[3 * k + 2] * flip / sz;
        const l = Math.hypot(nx, ny, nz) || 1; nrm[3 * k] = nx / l; nrm[3 * k + 1] = ny / l; nrm[3 * k + 2] = nz / l;
        puv[2 * k] = bx0 + (X + cxm - x0) / (x1 - x0) * (bx1 - bx0);
        puv[2 * k + 1] = 1 - (by0 + (y1 - Y) / (y1 - y0) * (by1 - by0));
        side[k] = nrm[3 * k + 2];
        // how far inside the photo's outline this point lands: near the cut edge the photo has arm shadow /
        // background bits, so those parts take plain fabric instead
        const dpx = distAt(puv[2 * k], 1 - puv[2 * k + 1]) / S * meta.N;
        edge[k] = THREE.MathUtils.smoothstep(dpx, 8, 30);
      }
      // smooth shading: average the normals of coincident vertices so the low-poly facets don't show as stripes
      const sum = new Map();
      for (let k = 0; k < n; k++) {
        const key = `${pos[3 * k].toFixed(4)},${pos[3 * k + 1].toFixed(4)},${pos[3 * k + 2].toFixed(4)}`;
        const a = sum.get(key) || [0, 0, 0]; a[0] += nrm[3 * k]; a[1] += nrm[3 * k + 1]; a[2] += nrm[3 * k + 2]; sum.set(key, a);
      }
      for (let k = 0; k < n; k++) {
        const a = sum.get(`${pos[3 * k].toFixed(4)},${pos[3 * k + 1].toFixed(4)},${pos[3 * k + 2].toFixed(4)}`), l = Math.hypot(...a) || 1;
        nrm[3 * k] = a[0] / l; nrm[3 * k + 1] = a[1] / l; nrm[3 * k + 2] = a[2] / l;
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      g.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
      g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(sh.uv), 2));
      g.setAttribute('puv', new THREE.BufferAttribute(puv, 2));
      g.setAttribute('side', new THREE.BufferAttribute(side, 1));
      g.setAttribute('edge', new THREE.BufferAttribute(edge, 1));
      g.setIndex(sh.index);
      // fabric colour for model areas the photo doesn't cover (sleeves cut differently): average of the opaque front
      const fc = document.createElement('canvas'); fc.width = fc.height = 32;
      const fx = fc.getContext('2d'); fx.drawImage(frontTex.image, 0, 0, 32, 32);
      const fd = fx.getImageData(0, 0, 32, 32).data; let fr = 0, fg = 0, fb = 0, fn = 0;
      for (let i = 0; i < fd.length; i += 4) if (fd[i + 3] > 200) { fr += fd[i]; fg += fd[i + 1]; fb += fd[i + 2]; fn++; }
      const fabric = new THREE.Color(`rgb(${Math.round(fr / fn)},${Math.round(fg / fn)},${Math.round(fb / fn)})`);
      const mat = new THREE.MeshStandardMaterial({ map: frontTex, roughness: 0.88, envMapIntensity: 0.5, side: THREE.DoubleSide });
      if (cfg.modelNormal) { mat.normalMap = await loadTex(cfg.modelNormal); mat.normalMap.colorSpace = THREE.NoColorSpace; mat.normalScale.set(0.8, 0.8); }
      mat.onBeforeCompile = s => {
        patch(s, cloth, true);
        Object.assign(s.uniforms, { uBack: { value: backTex }, uInside: { value: insideColor }, uFabric: { value: fabric } });
        s.vertexShader = s.vertexShader
          .replace('#include <common>', '#include <common>\nattribute vec2 puv; attribute float side, edge; varying vec2 vPuv; varying float vSide, vEdge;')
          .replace('#include <begin_vertex>', '#include <begin_vertex>\nvPuv = puv; vSide = side; vEdge = edge;');
        s.fragmentShader = s.fragmentShader
          .replace('#include <common>', '#include <common>\nuniform sampler2D uBack; uniform vec3 uInside; uniform vec3 uFabric; varying vec2 vPuv; varying float vSide, vEdge;')
          .replace('#include <map_fragment>', `
            vec4 cf = texture2D(map, vPuv), cb = texture2D(uBack, vPuv);
            vec3 f = mix(uFabric, cf.rgb, smoothstep(0.85, 1.0, cf.a)), b = mix(uFabric, cb.rgb, smoothstep(0.85, 1.0, cb.a));
            vec3 photo = mix(b, f, smoothstep(-0.2, 0.2, vSide));
            // the photo only where the model faces front/back and well inside the photo's outline; sides are plain fabric
            float w = vEdge * smoothstep(0.25, 0.6, abs(vSide));
            diffuseColor.rgb *= mix(uFabric, photo, w);
            if (!gl_FrontFacing) diffuseColor.rgb = uInside;`);
      };
      const depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
      depth.onBeforeCompile = s => patch(s, cloth, false);
      const m = new THREE.Mesh(g, mat);
      // no self-shadow: on this low-poly mesh the shadow map aliases into vertical stripes
      m.customDepthMaterial = depth; m.castShadow = true; m.receiveShadow = false;
      body.add(m); hitMeshes.push(m);
      // the hanger follows the model's own shoulder line (highest point per column), not the photo's outline
      const bins = new Map();
      for (let k = 0; k < n; k++) {
        const bx = Math.round(pos[3 * k] / S * 128) / 128, y = pos[3 * k + 1];
        if (!bins.has(bx) || bins.get(bx) < y) bins.set(bx, y);
      }
      modelTop = [...bins].map(([x, y]) => [x, topV - y / S]);
    }
    let modelTop = null;
    let faces = [];
    if (cfg.model) await modelMesh();
    else {
      // lite: the zoomed jersey swaps these two to the full-size textures (see sharpen)
      faces = [[jerseyMesh(frontTex, +1, 1.0), cfg.front], [jerseyMesh(backTex, -1, 0.85), cfg.back]]
        .map(([mesh, hiSrc]) => ({ mesh, lo: mesh.material.map, hiSrc, hi: null }));
      // the lining only shows at the collar and the dropped back tail: mesh just those bands
      if (liningTex) jerseyMesh(liningTex, +1, -0.7, false, v => v < topV + 0.2 || v > by1 - 0.12);        // inside of the back panel, seen through the open collar
    }

    // wooden hanger following the shoulder line
    let sx2 = 0, sx4 = 0, sy = 0, sx2y = 0, n = 0;
    for (const [x, v] of (modelTop || meta.top)) {
      const ax = Math.abs(x); if (v == null || ax < 0.09 || ax > 0.25) continue;
      const y = -(v - topV) * S, X2 = x * x;
      n++; sx2 += X2; sx4 += X2 * X2; sy += y; sx2y += X2 * y;
    }
    const b = (n * sx2y - sx2 * sy) / (n * sx4 - sx2 * sx2);
    const a = (sy - b * sx2) / n;
    // casual cuts have dropped shoulders and a pinched seam along the top: a shorter, lower bar stays inside
    const HALF = cfg.model ? 0.19 : VOL > 1 ? 0.2 : 0.235, R_WOOD = 0.016, DROP = cfg.model ? 0.05 : 0.036 + (VOL > 1 ? 0.016 : 0);
    // inside the shoulders; with an open collar the centre arches up so the wood crosses the neck in front of the rear collar
    const arch = cfg.lining ? 0.018 : 0;
    const shoulderY = x => a + b * x * x - DROP + arch * Math.exp(-((x / 0.08) ** 2));
    const pts = [];
    for (let i = 0; i <= 40; i++) { const x = -HALF + (2 * HALF) * i / 40; pts.push(new THREE.Vector3(x, shoulderY(x), 0)); }
    const bar = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 120, R_WOOD, 24, false), wood);
    bar.scale.z = 0.72; bar.castShadow = true; body.add(bar);
    for (const end of [pts[0], pts[pts.length - 1]]) {
      const cap = new THREE.Mesh(new THREE.SphereGeometry(R_WOOD, 24, 16), wood);
      cap.position.copy(end); cap.scale.z = 0.72; body.add(cap);
    }
    const neckY = shoulderY(0) + R_WOOD * 0.6;
    const block = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.02, 0.03, 24), wood);
    block.position.set(0, neckY + 0.012, 0); block.scale.z = 0.8; block.castShadow = true; body.add(block);
    const nut = new THREE.Mesh(new THREE.CylinderGeometry(0.009, 0.009, 0.012, 20), chrome);
    nut.position.set(0, neckY + 0.032, 0); body.add(nut);
    const HR = 0.023;
    const hc = [new THREE.Vector3(0, neckY + 0.03, 0), new THREE.Vector3(0, RAIL_Y - 0.075, 0), new THREE.Vector3(0, RAIL_Y - 0.035, HR * 0.8)];
    for (let deg = -15; deg <= 205; deg += 12) {          // front of the rail, over the top, down the back
      const r = THREE.MathUtils.degToRad(deg);
      hc.push(new THREE.Vector3(0, RAIL_Y + HR * Math.sin(r), HR * Math.cos(r)));
    }
    const hook = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(hc), 120, 0.0042, 12, false), chrome);
    hook.castShadow = true; body.add(hook);

    scene.add(pivot);
    return {
      cfg, index, pivot, swing, spin, cloth, hitMeshes, faces, baseX, x: baseX, xv: 0,
      theta: restFor(index), omega: 0, omegaPrev: 0, alpha: 0,
      twist: 0, twistV: 0, flare: 0, ripple: 0.002, swingX: 0, swingXV: 0, swingZ: 0, swingZV: 0, sleeve: 0, bend: 0,
      phase: index * 2.1, idleSince: 0,
    };
  }

  // ── Caption: the front-facing jersey, or the whole rail ─────────
  const brandEl = root.querySelector('[data-jersey-hanger-brand]');
  const nameEl = root.querySelector('[data-jersey-hanger-name]');
  const priceEl = nameEl.querySelector('[data-jersey-hanger-price]');
  const shopEl = root.querySelector('[data-jersey-hanger-shop]');
  const shopLabelEl = root.querySelector('[data-jersey-hanger-shop-label]');
  const hintEl = root.querySelector('[data-jersey-hanger-hint]');
  const closeEl = root.querySelector('[data-jersey-hanger-close]');
  const viewEl = root.querySelector('[data-jersey-hanger-view]');
  const canHover = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const defaults = {
    brand: brandEl.textContent, name: nameEl.firstChild.textContent, url: nameEl.getAttribute('href'),
    price: priceEl.textContent, shop: shopEl.getAttribute('href'),
  };
  let active = -1;                                         // -1: every jersey side-on
  let zoom = false, zoomT = 0, showBack = false;          // zoom: one jersey up close, centred
  function caption() {
    const j = jerseys[active];
    brandEl.textContent = j ? j.brand : defaults.brand;
    nameEl.setAttribute('href', j ? j.url : defaults.url);
    nameEl.firstChild.textContent = j ? `${j.name}${j.colour ? ' — ' + j.colour : ''}` : defaults.name;
    priceEl.textContent = j ? j.price : defaults.price;
    // SHOP JERSEYS (nothing chosen) / SHOP <BRAND>; zoomed there's also VIEW JERSEY for the product page
    shopEl.setAttribute('href', j ? j.shop : defaults.shop);
    shopLabelEl.textContent = j ? `${shopEl.dataset.labelBrand} ${j.brand.toUpperCase()}` : shopEl.dataset.labelAll;
    viewEl.hidden = !(zoom && j) || !!(j && j.current);   // product dialog: no link to the page you're on
    if (j) viewEl.setAttribute('href', j.url);
    // tell people what they can do next
    const d = hintEl.dataset;
    hintEl.textContent = zoom ? (canHover ? d.zoom : d.zoomTouch) : j ? (canHover ? d.active : d.activeTouch) : (canHover ? d.rail : d.railTouch);
    hintEl.classList.toggle('is-cue', !!j);
    closeEl.hidden = !zoom || single;
    root.classList.toggle('is-zoomed', zoom);
  }
  function retarget() {
    wake();
    if (!rigs) return;
    for (const r of rigs) {
      if (r.index !== active) { r.target = restFor(r.index); continue; }
      // zoomed, the camera is square on to the jersey; on the rail it faces the camera across the room
      r.target = (zoom ? 0 : facing(r.index)) + (zoom && showBack ? Math.PI : 0);
    }
  }
  function setActive(i) {
    if (i !== active) showBack = false;
    active = i;
    if (i < 0) zoom = false;
    retarget(); caption(); sharpen();
  }
  function setZoom(on) { zoom = on && active >= 0; if (!zoom) showBack = false; retarget(); caption(); sharpen(); }
  // lite: only the jersey you zoom into gets its full-size front and back; the rest stay at 512px
  function useMap(f, tex) { f.mesh.material.map = tex; f.mesh.customDepthMaterial.map = tex; }
  function sharpen() {
    if (!lite || !rigs) return;
    const want = zoom ? active : -1;
    for (const r of rigs) for (const f of r.faces) {
      if (r.index === want) {
        if (f.hi || f.loading) continue;
        f.loading = true;
        loadTex(f.hiSrc).then(t => {
          f.loading = false;
          if (!(zoom && active === r.index)) { t.dispose(); return; }
          f.hi = t; useMap(f, t); wake();
        }, () => { f.loading = false; });
      } else if (f.hi) {
        useMap(f, f.lo); f.hi.dispose(); f.hi = null;
      }
    }
  }
  const step1 = dir => {
    const n = jerseys.length;
    setActive(active < 0 ? (dir > 0 ? 0 : n - 1) : (active + dir + n) % n);
  };
  root.querySelector('[data-jersey-hanger-prev]').addEventListener('click', () => step1(-1));
  root.querySelector('[data-jersey-hanger-next]').addEventListener('click', () => step1(1));
  closeEl.addEventListener('click', () => { setZoom(false); setActive(-1); });
  root.addEventListener('keydown', e => { if (e.key === 'Escape' && zoom && !single) { setZoom(false); setActive(-1); } });

  let rigs = null;
  rigs = await Promise.all(jerseys.map((cfg, i) => buildJersey(cfg, i)));
  if (single || family) { active = family ? Math.min(+root.dataset.start || 0, jerseys.length - 1) : 0; zoom = true; zoomT = 1; }

  // ── Framing: whole rail, or zoomed on one jersey; rail under the header, hems above the caption ──
  const SPAN_TOP = RAIL_Y + 0.03;
  const SPAN_BOTTOM = -Math.max(...jerseys.map(j => j.meta.bbox[3] - j.meta.bbox[1])) - 0.02;
  const frame = { w: 1, h: 1, topPx: 0, bottomPx: 1, visRail: 1, visZoom: 1 };
  const canHoverResize = matchMedia('(hover: hover) and (pointer: fine)').matches;   // desktop windows resize for real
  let sizedW = 0, sizedH = 0;
  function resize() {
    const w = stage.clientWidth || innerWidth, h = stage.clientHeight || innerHeight;
    // Phones: scrolling shows/hides the address bar and nudges the slide height. Resizing the
    // canvas wipes it (the flicker), so keep its size and let the slide crop it instead.
    if (sizedW && w === sizedW && Math.abs(h - sizedH) < 160 && !canHoverResize) return;
    sizedW = w; sizedH = h;
    renderer.setSize(w, h, false);
    renderer.domElement.style.height = h + 'px';
    camera.aspect = w / h;
    const stageTop = stage.getBoundingClientRect().top + scrollY;
    const hv = parseFloat(getComputedStyle(root).getPropertyValue('--kave-header-height'));   // inherited; a page can override it
    const headerH = Number.isNaN(hv) ? 95 : hv;
    const foot = root.querySelector('.jersey-hanger__foot');
    const topPx = single ? 24 : family ? 64 : headerH + (small ? 52 : 64);
    const bottomPx = single ? h - 52 : Math.max(topPx + 200, foot.getBoundingClientRect().top + scrollY - stageTop - (small ? 16 : 28));
    const byHeight = (SPAN_TOP - SPAN_BOTTOM) * h / (bottomPx - topPx);
    // the whole rail, with room for a front-facing jersey and its pushed neighbours
    const needW = (jerseys.length - 1) * SPACING + (small ? 0.62 : 2 * PUSH + 0.95);
    Object.assign(frame, { w, h, topPx, bottomPx, visRail: Math.max(byHeight, needW / camera.aspect), visZoom: Math.max(byHeight, 1.05 / camera.aspect) });
    camDist = frame.visRail / 2 / Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
    retarget();
    applyCamera();
    renderer.shadowMap.needsUpdate = true;
    renderer.render(scene, camera);                          // redraw straight away: never show a wiped canvas
  }
  // the camera glides towards where the chosen jersey will settle, not where it is mid-slide
  let camX = 0;
  function applyCamera() {
    const { w, h, topPx, bottomPx } = frame;
    const e = zoomT * zoomT * (3 - 2 * zoomT);
    const visH = THREE.MathUtils.lerp(frame.visRail, frame.visZoom, e);
    const cx = camX * e;
    const dist = visH / 2 / Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
    camera.clearViewOffset();
    camera.position.set(cx, -0.3, dist);
    camera.lookAt(cx, (SPAN_TOP + SPAN_BOTTOM) / 2, 0);
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld();
    const railPx = (1 - new THREE.Vector3(cx, SPAN_TOP, 0).project(camera).y) / 2 * h;
    // when width decides the scale (phones), centre the rail in the band instead of leaving a gap below
    const spare = (bottomPx - topPx) - (SPAN_TOP - SPAN_BOTTOM) * h / visH;
    camera.setViewOffset(w, h, 0, railPx - topPx - Math.max(0, spare) / 2, w, h);
  }
  if (family) camX = rigs[active].baseX;                 // open already centred on the current colour
  resize();
  for (const r of rigs) r.theta = r.target;              // start at rest, no swing-in
  // intro: the rail starts empty and the jerseys run in from the right along it, one after another
  if (!single && !family && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const offRight = frame.visRail * camera.aspect / 2 + 0.25 - rigs[0].baseX;
    for (const r of rigs) { r.x = r.baseX + offRight; r.pivot.position.x = r.x; r.enterDelay = 0.25 + r.index * 0.075; r.entering = true; }
  }
  caption();                                              // touch or mouse wording from the start
  sharpen();
  new ResizeObserver(resize).observe(stage);

  // ── Input: which jersey is under the pointer ─────────────────────
  const el = renderer.domElement;
  const ray = new THREE.Raycaster(), ndc = new THREE.Vector2(), hit = new THREE.Vector3();
  const railPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  function indexAt(e) {
    const r = el.getBoundingClientRect();
    ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    ray.setFromCamera(ndc, camera);
    if (!ray.ray.intersectPlane(railPlane, hit)) return -1;
    if (hit.y > SPAN_TOP + 0.05 || hit.y < SPAN_BOTTOM - 0.05) return -1;
    // keep the front-facing jersey while the pointer is anywhere over it
    if (active >= 0 && Math.abs(hit.x - rigs[active].x) < 0.38) return active;
    let best = -1, bestD = Infinity;
    for (const rig of rigs) { const d = Math.abs(rig.x - hit.x); if (d < bestD) { bestD = d; best = rig.index; } }
    return bestD < 0.3 ? best : -1;
  }
  let scrubFrom = 0;
  const SCRUB_PX = 34;                                     // finger travel per jersey on the rail
  const EDGE_PX = 40;                                      // hero only: touches this close to a side change slide
  let drag = null, scrub = false, swipe = false, lastX = 0, lastT = 0, downX = 0, downIndex = -1;
  // in the hero, a touch that starts at the very edge of the screen belongs to the slider: it changes slide
  const heroSlide = root.closest('.kave-hero__slide');
  const atEdge = x => { if (!heroSlide) return false; const r = heroSlide.getBoundingClientRect(); return x - r.left < EDGE_PX || r.right - x < EDGE_PX; };
  // keep drags on the jerseys from swiping the hero slider
  el.addEventListener('mousedown', e => e.stopPropagation(), { passive: true });
  el.addEventListener('touchstart', e => { if (!atEdge(e.touches[0].clientX)) e.stopPropagation(); }, { passive: true });
  el.addEventListener('pointerdown', e => {
    downIndex = indexAt(e); downX = lastX = e.clientX; lastT = performance.now();
    if (e.pointerType !== 'mouse' && atEdge(e.clientX)) return;   // the slider has it; a plain tap still works below
    try { el.setPointerCapture(e.pointerId); } catch (_) { /* capture is a nicety, never a blocker */ }
    // touch on the rail: sliding a finger along it works like rollover
    // touch on the rail works like a carousel: swipe left brings in the next jersey, and the rail follows the finger
    if (!zoom && e.pointerType !== 'mouse') {
      scrub = true;
      scrubFrom = downIndex >= 0 ? downIndex : active >= 0 ? active : Math.floor(jerseys.length / 2);
      if (downIndex >= 0) setActive(downIndex);
      return;
    }
    // touch while zoomed: the middle turns the jersey, the sides swipe to the next / previous one
    if (zoom && !single && e.pointerType !== 'mouse') {
      const rx = (e.clientX - el.getBoundingClientRect().left) / el.clientWidth;
      if (!(downIndex === active && rx > 0.25 && rx < 0.75)) { swipe = true; return; }
    }
    if (downIndex >= 0 && downIndex === active) { drag = rigs[active]; el.classList.add('is-dragging'); }
  });
  el.addEventListener('pointermove', e => {
    if (drag || scrub) wake();
    if (scrub) {
      const i = THREE.MathUtils.clamp(scrubFrom - Math.round((e.clientX - downX) / SCRUB_PX), 0, jerseys.length - 1);
      if (i !== active && Math.abs(e.clientX - downX) > 8) setActive(i);
      return;
    }
    if (!drag) {
      // on the rail, rollover turns a jersey to the front; it stays chosen over empty space so its
      // buttons can be reached, until another jersey is hovered or the pointer leaves the slide
      if (e.pointerType === 'mouse' && !e.buttons) {
        const i = indexAt(e);
        if (!zoom && i >= 0) setActive(i);
        el.classList.toggle('is-pointer', !zoom && i >= 0);
      }
      return;
    }
    const now = performance.now(), dt = Math.max(1, now - lastT) / 1000;
    const d = (e.clientX - lastX) * 0.011;
    drag.theta += d;
    drag.omega = THREE.MathUtils.clamp(THREE.MathUtils.lerp(drag.omega, d / dt, 0.5), -5, 5);   // a fast flick coasts at most about half a turn
    lastX = e.clientX; lastT = now;
  });
  el.addEventListener('pointerup', e => {
    const tap = Math.abs(e.clientX - downX) < 6;
    const wasScrub = scrub; scrub = false;
    if (swipe) {
      swipe = false;
      const dx = e.clientX - downX;
      if (Math.abs(dx) > 40) { step1(dx < 0 ? 1 : -1); return; }  // swipe left → next jersey
    }
    if (wasScrub && !tap) return;                                     // finished a scrub: that jersey stays facing you
    if (drag) { drag.releasedAt = performance.now(); drag = null; el.classList.remove('is-dragging'); }
    if (!tap) return;
    if (!zoom) {
      // click / tap a jersey: bring it up close; empty space clears the rail
      if (downIndex >= 0) { setActive(downIndex); setZoom(true); } else setActive(-1);
      return;
    }
    if (downIndex === active) { showBack = !showBack; retarget(); }  // tap it again: turn to the back (and front again)
    else if (downIndex >= 0) setActive(downIndex);                    // a neighbour peeking in
    else if (!single) { setZoom(false); setActive(-1); }              // empty space: back to the rail
  });
  el.addEventListener('pointercancel', () => { scrub = false; swipe = false; if (drag) { drag.releasedAt = performance.now(); drag = null; el.classList.remove('is-dragging'); } });
  root.addEventListener('mouseleave', () => { if (!drag && !zoom) setActive(-1); });

  // ── Physics ──────────────────────────────────────────────────────
  let time = 0;
  function stepRig(r, dt) {
    // slide along the rail to make room for the front-facing jersey
    const push = PUSH + (zoom ? 0.14 : 0);                    // zoomed: a little more room around the jersey
    const goalX = r.goalX = r.baseX + (active < 0 || r.index === active ? 0 : (r.index < active ? -push : push));
    if (r.enterDelay > 0) { r.enterDelay -= dt; return; }    // intro: still waiting off-screen
    if (r.entering) {
      r.xv += ((goalX - r.x) * 7 - r.xv * 5.3) * dt;          // a slower, just-damped run in along the rail
      if (Math.abs(goalX - r.x) < 0.004 && Math.abs(r.xv) < 0.02) r.entering = false;
    } else r.xv += ((goalX - r.x) * 26 - r.xv * 10.2) * dt;  // soft, critically damped slide
    r.x += r.xv * dt;
    r.pivot.position.x = r.x;
    if (drag !== r) {
      // after a spin, let it coast for a moment before it swings back
      const coasting = r.releasedAt && performance.now() - r.releasedAt < 1200 && Math.abs(r.omega) > 0.4;
      if (coasting) {
        r.omega *= Math.exp(-1.7 * dt);
      } else {
        const goal = r.target + Math.round((r.theta - r.target) / (2 * Math.PI)) * 2 * Math.PI;   // the short way round
        r.omega += ((goal - r.theta) * 11 - r.omega * 5.4) * dt;        // eases round with just a hint of overshoot
      }
      r.theta += r.omega * dt;
    }
    r.alpha = THREE.MathUtils.lerp(r.alpha, (r.omega - r.omegaPrev) / dt, 0.35);
    r.omegaPrev = r.omega;
    const tw = -40 * r.twist - 3.0 * r.twistV - r.alpha * 0.055;   // lower cloth lags and swings back
    r.twistV += tw * dt; r.twist = THREE.MathUtils.clamp(r.twist + r.twistV * dt, -0.8, 0.8);
    r.flare = THREE.MathUtils.lerp(r.flare, Math.min(r.omega * r.omega * 0.012, 0.2), Math.min(1, dt * 3));
    const sxa = -24 * r.swingX - 1.7 * r.swingXV - r.alpha * 0.0022;
    r.swingXV += sxa * dt; r.swingX += r.swingXV * dt;
    // sliding along the rail rocks the hanger sideways
    const sza = -24 * r.swingZ - 1.7 * r.swingZV + r.alpha * 0.0014 - r.xv * 0.9;
    r.swingZV += sza * dt; r.swingZ += r.swingZV * dt;
    r.ripple = Math.min(0.018, r.ripple * Math.exp(-1.6 * dt) + Math.abs(r.alpha) * 0.00008 * dt * 60);
    r.sleeve = THREE.MathUtils.lerp(r.sleeve, THREE.MathUtils.clamp(-r.twistV * 0.02, -0.03, 0.03), 0.2);
    r.bend = THREE.MathUtils.lerp(r.bend, THREE.MathUtils.clamp(-r.swingXV * 0.05, -0.04, 0.04), 0.15);

    r.spin.rotation.y = r.theta;
    r.swing.rotation.x = r.swingX;
    r.swing.rotation.z = r.swingZ;
    const c = r.cloth;
    c.uTwist.value = r.twist;
    c.uFlare.value = r.flare; c.uRipple.value = r.ripple; c.uSleeve.value = r.sleeve; c.uBend.value = r.bend; c.uTime.value = time + r.phase;
  }
  function step(dt) {
    time += dt;
    for (const r of rigs) stepRig(r, dt);
    // glide the camera in / out of the zoom, following the jersey as it settles
    zoomT = THREE.MathUtils.clamp(zoomT + (zoom ? dt : -dt) * 2.2, 0, 1);
    const camGoal = active >= 0 ? rigs[active].baseX : camX;
    camX += (camGoal - camX) * (1 - Math.exp(-dt * 4.5));
    applyCamera();
    if (angleEl) {
      if (active < 0) angleEl.textContent = `${jerseys.length} ${angleEl.dataset.unit || 'JERSEYS'}`;
      else angleEl.textContent = String(active + 1).padStart(2, '0') + ' / ' + String(jerseys.length).padStart(2, '0');
    }
  }

  // ── Draw on demand: animate while anything moves, then stop until the next interaction ──
  function restless() {
    let e = Math.abs(zoomT - (zoom ? 1 : 0)) + Math.abs((active >= 0 ? rigs[active].baseX : camX) - camX);
    for (const r of rigs) {
      if (r.enterDelay > 0) e += 1;
      const goal = r.target + Math.round((r.theta - r.target) / (2 * Math.PI)) * 2 * Math.PI;
      e += Math.abs(goal - r.theta) + Math.abs(r.omega) + Math.abs(r.twist) + Math.abs(r.twistV) + Math.abs((r.goalX ?? r.x) - r.x) + Math.abs(r.xv)
        + Math.abs(r.swingX) + Math.abs(r.swingXV) + Math.abs(r.swingZ) + Math.abs(r.swingZV) + r.ripple * 10 + r.flare;
    }
    return e;
  }
  let onScreen = true;
  new IntersectionObserver(([entry]) => { onScreen = entry.isIntersecting; if (onScreen) wake(); }).observe(root);
  function tick(now) {
    const dt = Math.min(Math.max((now - last) / 1000, 1 / 240), 1 / 30);
    last = now;
    if (!isActive() || !onScreen) { running = false; renderer.setAnimationLoop(null); return; }
    step(dt);
    if (lite && (shadowFrame++ & 1) === 0) renderer.shadowMap.needsUpdate = true;
    renderer.render(scene, camera);
    if (!drag && restless() < 0.002) { if (++calm > 20) { running = false; renderer.setAnimationLoop(null); } }
    else calm = 0;
  }
  function wake() {
    calm = 0;
    if (running || !rigs || lost) return;
    running = true; last = performance.now();
    renderer.setAnimationLoop(tick);
  }
  // the GPU can drop the context (memory pressure, backgrounded tab): keep the page alive, pick up again when it's back
  renderer.domElement.addEventListener('webglcontextlost', e => {
    e.preventDefault(); lost = true; running = false; renderer.setAnimationLoop(null); root.classList.add('is-lost');
  });
  renderer.domElement.addEventListener('webglcontextrestored', () => { lost = false; root.classList.remove('is-lost'); wake(); });
  root.jerseyHangerWake = wake;
  wake();
  root.classList.add('is-ready');
}

function initAll() { document.querySelectorAll('[data-jersey-hanger]').forEach(init); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
else initAll();
document.addEventListener('shopify:section:load', initAll);
